// src/adminDashboard/shared/components/SkeletonLoader.tsx
// Skeleton loading states for various content types

import React from 'react';

interface SkeletonBaseProps {
    className?: string;
    style?: React.CSSProperties;
}

// Base skeleton with shimmer animation
const SkeletonBase: React.FC<SkeletonBaseProps> = ({ className = '', style }) => (
    <div className={`animate-pulse bg-slate-200 rounded ${className}`} style={style} />
);

interface SkeletonTextProps {
    lines?: number;
    className?: string;
}

// Text skeleton
export const SkeletonText: React.FC<SkeletonTextProps> = ({ lines = 1, className = '' }) => (
    <div className={`space-y-2 ${className}`}>
        {Array.from({ length: lines }).map((_, i) => (
            <SkeletonBase
                key={i}
                className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`}
            />
        ))}
    </div>
);

interface SkeletonAvatarProps {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
}

// Avatar skeleton
export const SkeletonAvatar: React.FC<SkeletonAvatarProps> = ({ size = 'md', className = '' }) => {
    const sizes = {
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12',
        xl: 'w-16 h-16',
    };
    return <SkeletonBase className={`${sizes[size]} rounded-full ${className}`} />;
};

interface SkeletonProps {
    className?: string;
}

// Card skeleton
export const SkeletonCard: React.FC<SkeletonProps> = ({ className = '' }) => (
    <div className={`bg-white rounded-xl border border-slate-200 p-6 ${className}`}>
        <div className="flex items-center gap-4 mb-4">
            <SkeletonAvatar size="md" />
            <div className="flex-1">
                <SkeletonBase className="h-4 w-32 mb-2" />
                <SkeletonBase className="h-3 w-24" />
            </div>
        </div>
        <SkeletonText lines={3} />
    </div>
);

// Stats card skeleton
export const SkeletonStatsCard: React.FC<SkeletonProps> = ({ className = '' }) => (
    <div className={`bg-white rounded-xl border border-slate-200 p-5 ${className}`}>
        <div className="flex items-center justify-between mb-3">
            <SkeletonBase className="h-4 w-24" />
            <SkeletonBase className="h-8 w-8 rounded-lg" />
        </div>
        <SkeletonBase className="h-8 w-20 mb-2" />
        <SkeletonBase className="h-3 w-16" />
    </div>
);

interface SkeletonTableRowProps {
    columns?: number;
    className?: string;
}

// Table row skeleton
export const SkeletonTableRow: React.FC<SkeletonTableRowProps> = ({ columns = 5, className = '' }) => (
    <tr className={`border-b border-slate-100 ${className}`}>
        {Array.from({ length: columns }).map((_, i) => (
            <td key={i} className="px-4 py-3">
                <SkeletonBase className="h-4 w-full" />
            </td>
        ))}
    </tr>
);

interface SkeletonTableProps {
    rows?: number;
    columns?: number;
    className?: string;
}

// Table skeleton
export const SkeletonTable: React.FC<SkeletonTableProps> = ({ rows = 5, columns = 5, className = '' }) => (
    <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${className}`}>
        <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                    {Array.from({ length: columns }).map((_, i) => (
                        <th key={i} className="px-4 py-3 text-left">
                            <SkeletonBase className="h-4 w-20" />
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: rows }).map((_, i) => (
                    <SkeletonTableRow key={i} columns={columns} />
                ))}
            </tbody>
        </table>
    </div>
);

interface SkeletonChartProps {
    height?: number;
    className?: string;
}

// Chart skeleton
export const SkeletonChart: React.FC<SkeletonChartProps> = ({ height = 320, className = '' }) => (
    <div className={`bg-white rounded-xl border border-slate-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
            <SkeletonBase className="h-5 w-32" />
            <SkeletonBase className="h-8 w-24 rounded-lg" />
        </div>
        <div className="relative" style={{ height }}>
            <SkeletonBase className="h-full w-full rounded-lg" />
            {/* Fake chart lines */}
            <div className="absolute inset-0 flex items-end justify-between px-4 pb-4 gap-2">
                {Array.from({ length: 12 }).map((_, i) => (
                    <SkeletonBase
                        key={i}
                        className="flex-1 bg-slate-300"
                        style={{ height: `${Math.random() * 60 + 20}%` }}
                    />
                ))}
            </div>
        </div>
    </div>
);

// Dashboard skeleton
export const SkeletonDashboard: React.FC = () => (
    <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonStatsCard key={i} />
            ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
                <SkeletonChart height={320} />
            </div>
            <SkeletonCard />
        </div>

        {/* Table */}
        <SkeletonTable rows={5} columns={5} />
    </div>
);

// List page skeleton
export const SkeletonListPage: React.FC = () => (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
            <div>
                <SkeletonBase className="h-7 w-40 mb-2" />
                <SkeletonBase className="h-4 w-24" />
            </div>
            <SkeletonBase className="h-10 w-32 rounded-lg" />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonStatsCard key={i} />
            ))}
        </div>

        {/* Search & Filters */}
        <div className="flex gap-3">
            <SkeletonBase className="h-10 flex-1 max-w-md rounded-lg" />
            <SkeletonBase className="h-10 w-24 rounded-lg" />
        </div>

        {/* Table */}
        <SkeletonTable rows={10} columns={6} />
    </div>
);

const Skeletons = {
    Text: SkeletonText,
    Avatar: SkeletonAvatar,
    Card: SkeletonCard,
    StatsCard: SkeletonStatsCard,
    TableRow: SkeletonTableRow,
    Table: SkeletonTable,
    Chart: SkeletonChart,
    Dashboard: SkeletonDashboard,
    ListPage: SkeletonListPage,
};

export default Skeletons;
